import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCommonSelector } from '@wajeez-web/stores/common';
import { InputLabel } from '@wajeez-web/components/ui';
import { TextAreaFormType } from '../Types';
import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';

export interface FormAreaTextInputProps<TFormFields extends FieldValues>
  extends UseControllerProps<TFormFields> {
  fieldType: TextAreaFormType;
  shape?: 'circle' | 'squre';
  color?: string;
  bg?: 'primary' | 'secondary' | string;
  disabled?: boolean;
  size?: 'sm' | 'med' | 'lg';
  prefix?: React.ReactElement;
  suffix?: JSX.Element | null;
  widthClassName?: string;
  showErrors?: boolean;
  errorMessages?: {
    required?: string;
    pattern?: string;
    minLength?: string;
  };
}

const FormAreaTextInput = <TFormFields extends FieldValues>({
  fieldType,
  shape = 'squre',
  color = 'textPrimary',
  bg = 'white',
  prefix,
  suffix,
  size,
  disabled,
  widthClassName,
  showErrors,
  errorMessages,
  ...props
}: FormAreaTextInputProps<TFormFields>) => {
  const { field, fieldState } = useController({
    ...props,
    rules: {
      required: fieldType.required ? 'Required' : undefined,
      minLength:
        fieldType.fieldOptions.minLength != undefined
          ? fieldType.fieldOptions.minLength
          : undefined,
      maxLength:
        fieldType.fieldOptions.maxLength != undefined
          ? fieldType.fieldOptions.maxLength
          : undefined,
      ...props.rules,
    },
  });

  const { dir } = useCommonSelector((state) => state.common.settings);

  const { t } = useTranslation();

  const isError = fieldState.error;

  switch (fieldState.error?.type) {
    case 'required':
      fieldState.error.message = errorMessages?.required;

      break;
    case 'pattern':
      fieldState.error.message = errorMessages?.pattern;

      break;
    case 'minLength':
      fieldState.error.message = errorMessages?.minLength;

      break;
    default:
      break;
  }

  return (
    <div className={`${widthClassName}`}>
      {fieldType.label && (
        <InputLabel
          text={
            t(
              `inputs.labels.${fieldType.label}`,
              fieldType.label.toString() || ''
            ) as string
          }
          name={field.name}
          required={fieldType.required}
        />
      )}
      <div
        className={`w-full relative ${
          shape === 'circle' ? 'rounded-full' : 'rounded-md'
        } shadow-sm`}
      >
        {prefix && (
          <div
            className={`absolute inset-y-1 ltr:left-0 rtl:right-0  pl-3 flex items-center pointer-events-none`}
          >
            <span className="text-gray-500 sm:text-sm">{prefix}</span>
          </div>
        )}
        <textarea
          rows={fieldType.fieldOptions.rows}
          required={fieldType.required}
          disabled={disabled}
          {...field}
          className={`w-full input focus:ring-ring-color  focus:ring-4 cursor-pointer  focus:ring-opacity-10 focus:ring-offset-0  ${
            shape === 'circle' ? 'rounded-full' : 'rounded-md'
          } bg-${bg} text-${color} focus:border-primary block w-full ${
            prefix && 'pl-7'
          } pe-12 sm:text-sm ${
            isError ? 'border-red-500' : 'border-gray-300'
          } `}
          placeholder={
            t(
              `inputs.placeholders.${fieldType.placeHolder}`,
              fieldType.placeHolder || ''
            ) as string
          }
        />
        <div
          className={`absolute h-auto inset-y-1 ltr:right-0 rtl:left-0  flex items-center`}
        >
          {suffix}
        </div>
      </div>
      {isError && showErrors && (
        <div className="mt-2">
          <p className="mb-0 text-sm text-red-500">{`${
            (t(`inputs.messages.${fieldState.error?.message}`),
            fieldState.error?.message)
          }`}</p>
        </div>
      )}
    </div>
  );
};

export default FormAreaTextInput;
